import { useMemo } from "react";
import { Translatable } from "Shared/TranslatableTextContent/translatable";

type UseTranslatableParams<T> = {
  textContent: Record<string, Record<string, string>> | null; // structure is { "en": { "buttonText": "Click me" }, "es": { "buttonText": "Haga clic en mí" } }
  useFallback?: boolean; // whether to fallback to english or config defaults if no translation is present
  language?: string | null; // if not provided, will use 'en''
  translatableConfiguration?: Record<string, Translatable>; // config defaults to use if no translation is present
  defaultLanguage?: string | null; // if not provided, will use 'en' as default language
}

export default function useTranslatable<T extends string | number | symbol>({
    textContent,
    useFallback = true,
    language = null,
    translatableConfiguration = {},
    defaultLanguage = null,
  }: UseTranslatableParams<T>): Record<T, string | null> {
  const languagePrefix = language || 'en'

  // structure is { "en": { "buttonText": "Click me" }, "es": { "buttonText": "Haga clic en mí" } }
  const content = useMemo<Record<T, string | null>>(() => {
    if (!textContent) return {} as Record<T, string | null>
    const thisLanguage = textContent[languagePrefix] || {}

    // We use the english text content as the default list, presuming that that will have all the keys in for now..
    // In future we may need to look at all of the languages to get a list of all possible keys.
    const textContentKeys = Object.values(textContent).flatMap(Object.keys);
    const configFallbackKeys = Object.keys(translatableConfiguration);
    const typeKeys: string[] = Object.keys({} as Record<T, any>);
    const allKeys = Array.from(new Set([...textContentKeys, ...configFallbackKeys, ...typeKeys]));

    const fallbackDefaultLanguage = textContent[defaultLanguage] || {};
    const fallbackEnglish = textContent['en'] || {}; //AS a final final resort, fallback to english as this is our default in some pre-filled action templates etc.
    return allKeys.reduce((acc, key) => {
      acc[key] = thisLanguage[key]
      if (useFallback && !acc[key]) {
        acc[key] = fallbackDefaultLanguage[key]
        if (translatableConfiguration && translatableConfiguration[key]) {
          acc[key] ||= translatableConfiguration[key]?.default || null
        }
        acc[key] ||= fallbackEnglish[key]
      }
      return acc;
    }, {} as Record<T, string | null>);
  }, [languagePrefix, JSON.stringify(textContent)])
  
  return content
}
